import React, { useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';

const GameContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 600px;
  position: relative;
  overflow: hidden; /* Hide any overflow content */
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
`;

const GameFrame = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  background: white;
  display: block; /* Prevent line height issues */
`;

const LoadingOverlay = styled.div<{ visible: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(255, 255, 255, 0.8);
  z-index: 10;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  pointer-events: ${props => props.visible ? 'all' : 'none'};
`;

export const SnakeGame: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Set a timeout to detect if the game takes too long to load
    timeoutRef.current = setTimeout(() => {
      if (loading) {
        console.error('Snake game loading timed out');
        setError('Game loading timed out. Please try refreshing.');
      }
    }, 5000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [loading]);

  const handleLoad = () => {
    setLoading(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Focus the iframe to ensure keyboard events work
    if (iframeRef.current) {
      iframeRef.current.focus();
      
      try {
        // Re-enable sound in the iframe
        if (iframeRef.current.contentWindow) {
          iframeRef.current.contentWindow.postMessage({ type: 'ENABLE_SOUND' }, '*');
          console.log("Snake game iframe loaded successfully");
        }
      } catch (e) {
        console.error("Error accessing iframe content:", e);
      }
    }
  };

  const handleError = () => {
    setLoading(false);
    setError('Failed to load the game. Please try refreshing.');
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    if (iframeRef.current) {
      iframeRef.current.src = '/game/snake.html';
    }
  };

  return (
    <GameContainer>
      <GameFrame 
        ref={iframeRef}
        src="/game/snake.html" 
        onLoad={handleLoad}
        onError={handleError}
        title="Snake Game"
        allow="fullscreen"
        sandbox="allow-scripts allow-same-origin"
      />
      
      <LoadingOverlay visible={loading || !!error}>
        {error ? (
          <div className="text-center">
            <p className="text-red-500 mb-4">{error}</p>
            <button
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
              onClick={handleRetry}
            >
              Retry
            </button>
          </div>
        ) : (
          <p className="text-gray-700">Loading Snake game...</p>
        )}
      </LoadingOverlay>
    </GameContainer>
  );
};

export default SnakeGame; 